import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useFolderTreeWithItems } from "@/hooks/queries";
import type { FolderItemOut, FolderOut, NoteCategory } from "@/lib/api";
import { useGraph } from "@/stores/graph";

import {
  copyFiles,
  createFolder,
  deleteFiles,
  deleteFolder,
  openFile,
  renameFile,
  renameFolder,
  setFolderColor,
} from "./file-actions";
import { flattenVisible } from "./FileTree";
import type { ContextMenuState } from "./FileContextMenu";
import { useFileSelection, type FileRef } from "./file-selection";
import { useMoveItems, type MoveTarget } from "./use-move-items";

/** A rename or new-folder input that is open somewhere in the tree. */
export type EditingState =
  | { kind: "rename-file"; ref: FileRef }
  | { kind: "rename-folder"; folder: FolderOut }
  //: A null category is a PROJECT folder — created in every category at once,
  //: so "Research" can hold markdown notes and handwriting and papers.
  | { kind: "new-folder"; category: NoteCategory | null; parent: string }
  | null;

/**
 * Everything a file-manager surface needs, in one hook: the tree, the selection,
 * the clipboard, the keyboard map, drag-drop, and the context menu.
 *
 * Both the left rail and Folder View mount this, so the two cannot diverge in
 * behaviour the way the app's two file trees previously did. A surface decides
 * only how to LOOK.
 */
export function useFileManager({ enableShortcuts = true }: { enableShortcuts?: boolean } = {}) {
  const tree = useFolderTreeWithItems();
  const move = useMoveItems();
  const [menu, setMenu] = useState<ContextMenuState>(null);
  const [editing, setEditing] = useState<EditingState>(null);
  const [confirmDelete, setConfirmDelete] = useState<FileRef[] | null>(null);

  const roots = useMemo(() => tree.data?.categories ?? [], [tree.data]);
  const clipboard = useFileSelection((s) => s.clipboard);

  const dropInto = useCallback(
    (refs: FileRef[], target: MoveTarget) => move.mutate({ refs, target }),
    [move],
  );

  /** Paste. A cut MOVES the originals; a copy DUPLICATES them into the folder,
   * named with the vault's own collision scheme (`Notes` → `Notes (2)`), which
   * is why the two branches look so different: one re-files rows, the other
   * asks the server to make new ones. */
  const paste = useCallback(
    (folder: FolderOut) => {
      if (!clipboard) return;
      if (clipboard.mode === "copy") {
        // A copy is always legal — a duplicate of a PDF is refused server-side
        // with a reason, and pasting into another category is fine because the
        // copy keeps its own.
        void copyFiles(clipboard.refs, folder.path);
        useFileSelection.getState().clear();
        return;
      }
      const target: MoveTarget = { category: folder.category, folder: folder.path };
      const movable = clipboard.refs.filter(
        (r) => r.category === target.category && r.folder !== target.folder,
      );
      if (!movable.length) {
        toast("Nothing to paste here", {
          description: "Those files are already in this folder, or belong to another category.",
        });
        return;
      }
      move.mutate({ refs: movable, target });
      // Clear BOTH: the clipboard because it has been spent, and the selection
      // because those files now live in a folder the user is not looking at —
      // a "3 selected" bar pointing at rows that are no longer on screen is a
      // selection you cannot see the extent of.
      useFileSelection.getState().clearClipboard();
      useFileSelection.getState().clear();
    },
    [clipboard, move],
  );

  // ── keyboard ────────────────────────────────────────────────────────────
  // Only the operations that MEAN something in a browser file manager. There is
  // deliberately no attempt to mirror every OS shortcut: Ctrl+Z is the editor's,
  // and a system clipboard cannot carry a vault entry, so Ctrl+C/X are in-app
  // and say so.
  useEffect(() => {
    if (!enableShortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never steal a key from something being typed into.
      if (
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName ?? "")
      ) {
        return;
      }
      const store = useFileSelection.getState();
      const selected = store.selectedRefs();
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === "a") {
        const all = flattenVisible(roots, () => true);
        if (!all.length) return;
        e.preventDefault();
        store.setSelection(all);
        return;
      }
      if (!selected.length) return;

      if (mod && e.key.toLowerCase() === "x") {
        e.preventDefault();
        store.cut(selected);
        toast(`Cut ${selected.length} file${selected.length === 1 ? "" : "s"}`, {
          description: "Right-click a folder and choose Paste, or press Ctrl+V there.",
        });
        return;
      }
      if (mod && e.key.toLowerCase() === "c") {
        e.preventDefault();
        store.copy(selected);
        return;
      }
      if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        void copyFiles(selected);
        return;
      }
      if (e.key === "Escape") {
        store.clear();
        store.clearClipboard();
        return;
      }
      if (e.key === "Delete") {
        e.preventDefault();
        setConfirmDelete(selected);
        return;
      }
      if (e.key === "F2" && selected.length === 1) {
        e.preventDefault();
        setEditing({ kind: "rename-file", ref: selected[0]! });
        return;
      }
      if (e.key === "Enter" && selected.length === 1) {
        e.preventDefault();
        const ref = selected[0]!;
        openFile({ id: ref.id, kind: ref.kind, title: ref.title });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enableShortcuts, roots]);

  // A selection that outlives the surface it was made on is a selection acting
  // on things the user can no longer see.
  useEffect(() => () => useFileSelection.getState().clear(), []);

  // Selecting ONE file shows it in the inspector. The panel is keyed by graph
  // node id and could previously only be reached by clicking a star in the
  // atlas — so the details, description and tags of a note were invisible from
  // the very surface where you browse notes. A multi-selection deliberately
  // does not: the panel describes one thing, and showing an arbitrary member of
  // a five-file selection would be worse than showing none.
  const selectedCount = useFileSelection((s) => s.selected.size);
  const soleNodeId = useFileSelection((s) =>
    s.selected.size === 1 ? ([...s.selected.values()][0]?.nodeId ?? null) : null,
  );
  useEffect(() => {
    if (selectedCount === 1 && soleNodeId) {
      useGraph.getState().select({ kind: "node", id: soleNodeId });
    }
  }, [selectedCount, soleNodeId]);

  const commitEditing = useCallback(
    async (value: string) => {
      const current = editing;
      setEditing(null);
      if (!current) return;
      if (current.kind === "rename-file") await renameFile(current.ref, value);
      else if (current.kind === "rename-folder" && current.folder.id) {
        const parent = current.folder.path.includes("/")
          ? current.folder.path.slice(0, current.folder.path.lastIndexOf("/"))
          : "";
        const cleaned = value.trim();
        if (cleaned) await renameFolder(current.folder.id, parent ? `${parent}/${cleaned}` : cleaned);
      } else if (current.kind === "new-folder") {
        const cleaned = value.trim();
        if (cleaned) {
          await createFolder(
            current.category,
            current.parent ? `${current.parent}/${cleaned}` : cleaned,
          );
        }
      }
    },
    [editing],
  );

  return {
    tree,
    roots,
    move,
    menu,
    setMenu,
    editing,
    setEditing,
    commitEditing,
    confirmDelete,
    setConfirmDelete,
    clipboard,
    dropInto,
    paste,
    openItem: (item: FolderItemOut) => openFile(item),
    /** Duplicate in place — the copy lands beside its original. */
    duplicate: (refs: FileRef[]) => void copyFiles(refs),
    /** Folder actions, pre-bound so a surface just wires them to its menu. */
    folderActions: {
      rename: (folder: FolderOut) => setEditing({ kind: "rename-folder", folder }),
      newSubfolder: (folder: FolderOut) =>
        setEditing({ kind: "new-folder", category: folder.category, parent: folder.path }),
      setColor: (folder: FolderOut, color: string | null) => {
        // A DERIVED folder (one that exists only because items say they live
        // there) has no row to hang a colour on yet. Creating it WITH the colour
        // is one round trip and cannot lose the colour between two calls.
        if (folder.id) void setFolderColor(folder.id, color);
        else void createFolder(folder.category, folder.path, color);
      },
      remove: (folder: FolderOut) => (folder.id ? void deleteFolder(folder.id) : undefined),
    },
    deleteSelected: async (refs: FileRef[]) => {
      setConfirmDelete(null);
      await deleteFiles(refs);
    },
  };
}
